// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Paper, Stack, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Patient, QuestionnaireResponse } from '@medplum/fhirtypes';
import { Document, Loading, QuestionnaireResponseDisplay, useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageHeader } from '../../components/layout/PageHeader';
import { botIdentifier } from '../../lib/bot-identifier';

export function IntakeReviewPage(): JSX.Element {
  const { questionnaireResponseId } = useParams();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [response, setResponse] = useState<QuestionnaireResponse>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!questionnaireResponseId) {
      setLoading(false);
      return;
    }

    medplum
      .readResource('QuestionnaireResponse', questionnaireResponseId)
      .then(setResponse)
      .catch((error) => {
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      })
      .finally(() => setLoading(false));
  }, [medplum, questionnaireResponseId]);

  const handleApprove = useCallback(async () => {
    if (!response) {
      return;
    }

    setSubmitting(true);
    try {
      const patientRef = response.subject?.reference;
      let patient: Patient | undefined;
      if (patientRef?.startsWith('Patient/')) {
        const patientId = patientRef.replace('Patient/', '');
        patient = await medplum.readResource('Patient', patientId);
      }

      // Update QuestionnaireResponse status to completed
      await medplum.updateResource<QuestionnaireResponse>({
        ...response,
        status: 'completed',
      });

      // Invite via the idempotent admin bot (sendEmail: false — welcome email is
      // sent by the send-patient-welcome-email subscription on UserSecurityRequest
      // create). If the patient was already invited at booking time, this is a
      // safe no-op: no 409, no duplicate membership, no second welcome email.
      //
      // Wrapped in its own try/catch so an invite failure does NOT roll back the
      // Patient activation and QR completion that already succeeded above. Staff
      // can click Approve again to retry — the idempotent bot makes that safe.
      if (patient) {
        try {
          await medplum.executeBot(botIdentifier('invite-patient'), { patientReference: createReference(patient) });
        } catch (inviteError) {
          console.error('[IntakeReviewPage] invite-patient bot failed (best-effort)', inviteError);
          Sentry.captureException(inviteError, { tags: { bot: 'invite-patient' } });
          showNotification({
            color: 'orange',
            message: 'Patient approved, but the platform invite failed — click Approve again to retry.',
            autoClose: false,
          });
          return;
        }
      }

      showNotification({
        color: 'green',
        message: 'Patient approved',
      });

      await navigate('/triage');
    } catch (error) {
      showNotification({
        color: 'red',
        message: normalizeErrorString(error),
        autoClose: false,
      });
    } finally {
      setSubmitting(false);
    }
  }, [medplum, navigate, response]);

  const handleDecline = useCallback(async () => {
    if (!response) {
      return;
    }

    setSubmitting(true);
    try {
      // Update QuestionnaireResponse status to completed
      await medplum.updateResource<QuestionnaireResponse>({
        ...response,
        status: 'completed',
      });

      showNotification({
        color: 'yellow',
        message: 'Intake declined',
      });

      await navigate('/triage');
    } catch (error) {
      showNotification({
        color: 'red',
        message: normalizeErrorString(error),
        autoClose: false,
      });
    } finally {
      setSubmitting(false);
    }
  }, [medplum, navigate, response]);

  if (loading) {
    return <Loading />;
  }

  if (!response) {
    return (
      <Document>
        <Title order={2}>Questionnaire Response not found</Title>
      </Document>
    );
  }

  return (
    <Document>
      <Stack gap="md">
        <PageHeader title="Review Intake Form" />

        <Paper withBorder radius="md" p="md">
          <QuestionnaireResponseDisplay questionnaireResponse={response} />
        </Paper>

        <Group justify="flex-end">
          <Button variant="outline" color="red" onClick={handleDecline} loading={submitting}>
            Decline
          </Button>
          <Button color="green" onClick={handleApprove} loading={submitting}>
            Approve
          </Button>
        </Group>
      </Stack>
    </Document>
  );
}
